// AdminGitAccessPage — SB-058/SB-057 立面；git 凭据体系改造（内外双线裁定批）
// 换成凭据/授权两级：一枚凭据（username+token，明文只在签发响应里出现一次）
// 下挂多条 grant（仓×读写，吊销留行翻位）。
//
// ── 本批的三处结构变化（为什么，不只是什么）────────────────────────────────
// · 仓名从自由文本换成下拉（数据源 /api/admin/git-repos，服务端已滤敏感仓）：
//   写错仓名的凭据在默认拒绝的闸上表现为"好像发成功了但推不上去"——那种错
//   要在下拉里就不可能发生，而不是等外包方来报。
// · 凭据可绑归属账号（accountId）：密送分支的收件人就是它，不再单设一个
//   "收件账号"下拉——同一个人选两遍是把选错的机会给了两次。
// · 行内动作全走新端点族（grant 加/吊/翻写位、凭据吊销）；旧 /api/admin/git-access
//   三只后端保留兼容，本页不再调（gitAccess.test.mjs 的结构判据咬着这一条）。
import { useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, InputLabel,
  MenuItem, Paper, Select, Stack, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import { useLoad } from '../hooks/useLoad.mjs';
import { useLang, useT } from '../i18n/index.mjs';
import AdminSubnav from '../components/AdminSubnav.jsx';
import DataTable from '../components/DataTable.jsx';
import HelpLink from '../components/HelpLink.jsx';
import {
  credentialRows, gitIssueResultKind, grantableRepos, introBlocker, issueBlocker,
  repoOptions, usageView,
} from './gitAccess.mjs';
// 第二百零七批（SB-180 (e) 实撞）：密送落点组——多组收件人必须显式选定，
// 判断在 deliverGroup.mjs 一处定形，API key 发放面共用同一份。
import {
  deliveryGroupOptions, deliveryToGroupId, groupListOf, needsGroupChoice,
} from './deliverGroup.mjs';

function fmtMs(ms) {
  if (ms == null) return '—';
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return String(ms);
  }
}

export default function AdminGitAccessPage() {
  const t = useT();
  const { lang } = useLang();
  const { snack, notify, clearSnack } = useNotify();

  const repos = useLoad(() => api.adminGitRepos(), []);
  const repoList = repoOptions(repos.data);
  const creds = useLoad(() => api.adminGitCredentials(), []);
  const credList = credentialRows(creds.data);
  // 账号清单服务两处：归属账号下拉（发放表单）与凭据行上的归属显示。
  // SB-147：选人必须见邮箱，显示名不是身份。
  const accounts = useLoad(() => api.adminListAccounts(), []);
  const accountList = Array.isArray(accounts.data?.accounts) ? accounts.data.accounts : [];
  // 第二百零七批（SB-180 (e)）：组列表只为密送落点组下拉取名——账号行里只有
  // 组 id，光靠它下拉里全是 #3 这种谜语。取不到时选项回落 `#<id>`，下拉照常可选。
  const groups = useLoad(() => api.adminProjectGroups().catch(() => null), []);
  const groupList = groupListOf(groups.data);
  const now = Date.now();

  // ── 发放表单 ──────────────────────────────────────────────────────────────
  const [username, setUsername] = useState('');
  // 第一百七十五批：发放前即可多选仓（后端 grants 数组，随凭据原子落库）。
  // 写位在发放面是**整单一致**的（勾了 = 所选各仓都可写）：逐仓分写位是授权面
  // 的日常动作（卡片行内翻位，令牌不变），发放表单不复制那套控件。
  const [selRepos, setSelRepos] = useState([]);
  // 默认**只读**（旧页默认可写）：本批通用只读令牌是裁定的常态，写权是例外——
  // 默认值就该长成常态的样子，让例外成为一次显式的勾选。
  const [canWrite, setCanWrite] = useState(false);
  const [label, setLabel] = useState('');
  const [accountId, setAccountId] = useState('');
  const [deliver, setDeliver] = useState(false);
  // 第二百零七批（SB-180 (e) 实撞）：密送落点组的选择。多组收件人不选就发正是
  // 撞过的形状（后端自动取首组，distill 凭据落进 MirrorMii 组）——现在多组必选，
  // 单组不展示下拉但请求照带唯一组 id。
  const [deliverGroupId, setDeliverGroupId] = useState('');
  const [busy, setBusy] = useState(false);
  // The one-time token display (never retrievable again).
  const [issued, setIssued] = useState(null);
  const issuedKind = gitIssueResultKind(issued);

  const owner = accountList.find((a) => String(a.id) === String(accountId));
  const toGroupId = deliveryToGroupId(owner, deliverGroupId);
  const blocker = issueBlocker({
    username, repos: selRepos, deliver, accountId, account: owner, toGroupId: deliverGroupId,
  });

  const errMsg = (e, fallback) =>
    t(errKey(e, { byStatus: { 409: 'admin.git.conflict' }, fallback }));
  // grant 动作的 409 不是"用户名已持凭据"——那句是签发面的话，映到这里会指错路。
  const grantErrMsg = (e) =>
    t(errKey(e, { byStatus: { 409: 'admin.git.grantConflict' }, fallback: 'admin.git.error' }));
  // 介绍信的 409 分两种：站内信「同一目标不叠排」（有码 DM_ALREADY_QUEUED，
  // 下一步是等/撤上一封）与模板自己的拒绝（授权面变了，下一步是刷新看卡片）。
  // 两句的动作完全相反，靠 byCode 先于 byStatus 分开。
  const introErrMsg = (e) =>
    t(errKey(e, {
      byCode: { DM_ALREADY_QUEUED: 'admin.git.introQueued' },
      byStatus: { 409: 'admin.git.introConflict' },
      fallback: 'admin.git.error',
    }));

  const reloadAll = () => {
    creds.reload();
    repos.reload();   // enabledGrants 计数随每次授权变动
  };

  const onIssue = async () => {
    if (busy || blocker) return;
    setBusy(true);
    setIssued(null);
    try {
      // 单发原子（第一百七十五批，取代此前的两段式）：凭据与所选各仓的授权在
      // 后端同一 savepoint 落库——要么整单成立，要么什么都没发生。密送信里因此
      // 列的是**真仓单**（逐仓克隆地址＋读写），不再是占位符。
      const r = await api.adminGitCredentialIssue({
        username: username.trim(), label: label.trim(),
        grants: selRepos.map((name) => ({ repo: name, canWrite })),
        ...(accountId ? { accountId: Number(accountId) } : {}),
        // 落点组显式随单（第二百零七批）：单组收件人也带——显式落点让后端对
        // 单组/多组走同一条校验路，不留一半继续走"自动取首组"的老路。
        ...(deliver && !blocker
          ? { deliverInstead: true, ...(toGroupId != null ? { toGroupId } : {}) }
          : {}),
      });
      setIssued(r);
      setUsername('');
      setLabel('');
      setSelRepos([]);
      notify(t('admin.git.issued'), 'success');
      reloadAll();
    } catch (e) {
      notify(errMsg(e, 'admin.git.error'), 'error');
    } finally {
      setBusy(false);
    }
  };

  // ── 行内动作（grant 三件 + 凭据吊销）。全页共用一个 busy：要防的是并发写，
  //    这四个动作彼此之间也一样会打架。 ──────────────────────────────────────
  // 返回"成没成"：onAddGrant 要拿它决定清不清表单——失败时清掉等于把这个人
  // 刚选好的仓与写位丢了，还让失败看起来像成功了一半。
  const run = async (fn, okKey, mapErr = grantErrMsg) => {
    if (busy) return false;
    setBusy(true);
    try {
      await fn();
      notify(t(okKey), 'success');
      reloadAll();
      return true;
    } catch (e) {
      notify(mapErr(e), 'error');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // 每凭据一份加-grant 表单（credId → {repo, canWrite}）；不并进全局表单——
  // 两枚凭据各自选到一半互相覆盖是最难被注意到的一种串味。
  const [grantForm, setGrantForm] = useState({});
  // 第一百七十七批（SB-180 (d)）：未绑定凭据的绑定选择（credId → accountId）。
  // 与 grantForm 同一条理由分凭据存放：两枚各自选到一半互相覆盖是最难察觉的串味。
  const [bindSel, setBindSel] = useState({});
  const gf = (c) => grantForm[c.id] ?? { repo: '', canWrite: false };
  const setGf = (c, patch) =>
    setGrantForm((m) => ({ ...m, [c.id]: { ...gf(c), ...patch } }));

  const onAddGrant = async (c) => {
    const f = gf(c);
    if (!f.repo) return;
    const ok = await run(
      () => api.adminGitGrantAdd(c.id, { repo: f.repo, canWrite: Boolean(f.canWrite) }),
      'admin.git.saved',
    );
    if (ok) setGrantForm((m) => ({ ...m, [c.id]: { repo: '', canWrite: false } }));
  };

  const usageText = (c) => {
    const u = usageView(c, now);
    return u.rel
      ? t('admin.git.usage', {
          s: `${t(u.rel.key, u.rel.vars)} · ${t('admin.git.usage.count', { n: u.count })}`,
        })
      : t('admin.git.usage', { s: t('admin.git.usage.never') });
  };

  const ownerEmailOf = (c) => {
    if (c.accountId == null) return null;
    const a = accountList.find((x) => String(x.id) === String(c.accountId));
    return a ? a.email : `#${c.accountId}`;
  };

  return (
    <Box>
      <AdminSubnav />
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
        {t('admin.git.title')}
        <HelpLink section="admin" />
      </Typography>

      {/* ── 发放 ─────────────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {t('admin.git.issue')}
        </Typography>
        <Stack sx={{ alignItems: { md: 'center' } }} direction={{ xs: 'column', md: 'row' }}
          spacing={2} useFlexGap>
          <TextField size="small" label={t('admin.git.username')} value={username}
            onChange={(e) => setUsername(e.target.value)} disabled={busy} />
          <FormControl size="small"
            sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 260 } }}>
            <InputLabel id="git-repo-label">{t('admin.git.repos')}</InputLabel>
            {/* 多选（第一百七十五批）：value 恒为数组；renderValue 逗号连写——
                选中态要在关着的框里看得全，而不是数一个"已选 2 项"的谜语。 */}
            <Select
              labelId="git-repo-label"
              label={t('admin.git.repos')}
              multiple
              value={selRepos}
              onChange={(e) => {
                const v = e.target.value;
                setSelRepos(Array.isArray(v) ? v : [v]);
              }}
              renderValue={(selected) => selected.join(', ')}
              // 仓单为空 ⇒ 禁用 + 下面一句说清去哪配置，而不是留一个点开没有
              // 任何选项的哑下拉。
              disabled={busy || repoList.length === 0}
            >
              {repoList.map((r) => (
                <MenuItem key={r.repo} value={r.repo} sx={{ whiteSpace: 'normal' }}>
                  <Checkbox size="small" checked={selRepos.includes(r.repo)}
                    sx={{ py: 0, pl: 0 }} />
                  {r.repo}
                  {r.enabledGrants > 0 ? (
                    <Typography component="span" variant="caption" color="text.secondary"
                      sx={{ ml: 1 }}>
                      {t('admin.git.repoGrants', { n: r.enabledGrants })}
                    </Typography>
                  ) : null}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small"
            sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 260 } }}>
            <InputLabel id="git-account-label">{t('admin.git.account')}</InputLabel>
            <Select
              labelId="git-account-label"
              label={t('admin.git.account')}
              value={accountId}
              // 换收件人必须清落点组选择：上一位的组 id 对这一位可能不存在，
              // 更糟的是**恰好也存在**——那就是把密件落进错组的第二种走法。
              onChange={(e) => { setAccountId(e.target.value); setDeliverGroupId(''); setIssued(null); }}
              disabled={busy}
            >
              <MenuItem value="">{t('admin.git.accountNone')}</MenuItem>
              {accountList.map((a) => (
                // whiteSpace normal：Popover 纸面横向只裁不滚，「名字 · 邮箱」在
                // 360px 下会被静默裁掉（AdminApiKeysPage 同注）。
                <MenuItem key={a.id} value={a.id} sx={{ whiteSpace: 'normal' }}>
                  {a.displayName ? `${a.displayName} · ${a.email}` : a.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField size="small" label={t('admin.git.label')} value={label}
            onChange={(e) => setLabel(e.target.value)} disabled={busy} />
          {/* 多选后写位整单一致：标签把"所选各仓"说出口——逐仓分写位去卡片
              行内翻，令牌不变。 */}
          <FormControlLabel
            control={<Checkbox checked={canWrite} onChange={(e) => setCanWrite(e.target.checked)}
              disabled={busy} />}
            label={t('admin.git.canWriteAll')}
          />
          <Button variant="contained" onClick={onIssue} disabled={busy || Boolean(blocker)}>
            {t('admin.git.issue')}
          </Button>
        </Stack>
        {repos.status === 'ready' && repoList.length === 0 ? (
          <Alert severity="warning" sx={{ mt: 1 }}>{t('admin.git.repoEmpty')}</Alert>
        ) : null}

        {/* ── 密送分支（第一百三十五批需求方裁；收件人=归属账号）────────────── */}
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={deliver}
                disabled={busy}
                onChange={(e) => { setDeliver(e.target.checked); setIssued(null); }}
              />
            }
            label={t('admin.git.deliverInstead')}
          />
          {deliver ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {blocker === 'recipient' ? t('admin.git.deliverNeedsAccount')
                : blocker === 'noGroup' ? t('admin.git.noGroup')
                : blocker === 'group' ? t('admin.git.deliverNeedsGroup')
                : t('admin.git.deliverHelper')}
            </Typography>
          ) : null}
          {/* ── 落点组下拉（第二百零七批，SB-180 (e) 实撞）：只对多组收件人出现，
              必选。单组收件人不出下拉——唯一落点没有选错的可能，请求仍带唯一
              组 id（toGroupId 显式随单）。选项名取自组列表，取不到回落 #id。 */}
          {deliver && needsGroupChoice(owner) ? (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
              sx={{ mt: 1, alignItems: { md: 'center' } }}>
              <FormControl size="small"
                sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 260 } }}>
                <InputLabel id="git-deliver-group-label">{t('admin.git.deliverGroup')}</InputLabel>
                <Select
                  labelId="git-deliver-group-label"
                  label={t('admin.git.deliverGroup')}
                  value={deliverGroupId}
                  onChange={(e) => { setDeliverGroupId(e.target.value); setIssued(null); }}
                  disabled={busy}
                >
                  {deliveryGroupOptions(owner, groupList, lang).map((o) => (
                    <MenuItem key={o.id} value={o.id} sx={{ whiteSpace: 'normal' }}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                {t('admin.git.deliverGroupHint')}
              </Typography>
            </Stack>
          ) : null}
        </Box>

        {issuedKind === 'delivered' ? (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setIssued(null)}>
            {t('admin.git.delivered', {
              id: issued.delivery.id,
              who: issued.delivery.toAccount || '—',
              t: fmtMs(issued.delivery.expiresAtMs),
            })}
            <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
              {t('admin.git.deliveredHint')}
            </Box>
          </Alert>
        ) : issuedKind === 'plaintext' ? (
          <Alert severity="warning" sx={{ mt: 2, wordBreak: 'break-all' }}
            onClose={() => setIssued(null)}>
            {t('admin.git.tokenOnce')}
            <Box component="code" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
              {issued.username} : {issued.token}
            </Box>
          </Alert>
        ) : issued ? (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setIssued(null)}>
            {t('admin.git.error')}
          </Alert>
        ) : null}
      </Paper>

      {/* ── 凭据列表（每凭据一卡：头行 + grants 子表 + 加授权行）──────────── */}
      {creds.status === 'error' ? (
        <Alert severity="error">{errMsg(creds.error, 'admin.git.error')}</Alert>
      ) : credList.length === 0 && creds.status === 'ready' ? (
        <Typography color="text.secondary">{t('admin.git.empty')}</Typography>
      ) : (
        credList.map((c) => (
          <Paper key={c.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}
              sx={{ justifyContent: 'space-between', alignItems: { md: 'center' } }}>
              <Stack direction="row" spacing={1}
                sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
                <Typography sx={{ fontWeight: 600 }}>{c.username}</Typography>
                {/* 服务凭据徽记：它背后是跑着的系统组件（backend-bot 一族），
                    吊了它坏的不是某个人的克隆，是一条流水线。 */}
                {c.isService ? (
                  <Chip size="small" color="info" label={t('admin.git.service')} />
                ) : null}
                <Chip size="small"
                  color={c.enabled ? 'success' : 'default'}
                  label={c.enabled ? t('admin.git.active') : t('admin.git.revoked')} />
                {c.label ? (
                  <Typography variant="body2" color="text.secondary">{c.label}</Typography>
                ) : null}
                {ownerEmailOf(c) ? (
                  <Typography variant="caption" color="text.secondary">
                    {t('admin.git.boundTo', { who: ownerEmailOf(c) })}
                  </Typography>
                ) : null}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">{usageText(c)}</Typography>
                {/* 「发送用法介绍信」立在吊销**前面**（需求方裁）：教人用在先、
                    收权在后。发不出时按钮禁用＋悬停说清为什么（unbound/noGrants
                    与后端拒绝面同判据；服务端仍是闸）。信是系统模板（SB-148），
                    正文由服务端从当下生效授权渲染，**不含令牌明文**。 */}
                {c.enabled ? (() => {
                  const ib = introBlocker(c);
                  const btn = (
                    <Button size="small" disabled={busy || Boolean(ib)}
                      onClick={() => run(
                        () => api.adminSendTemplateMessage({
                          toAccount: Number(c.accountId),
                          template: 'git-credential-intro',
                          context: { credentialId: c.id },
                        }),
                        'admin.git.introSent', introErrMsg)}>
                      {t('admin.git.intro')}
                    </Button>
                  );
                  return ib ? (
                    <Tooltip title={t(`admin.git.intro.${ib}`)}>
                      <span>{btn}</span>
                    </Tooltip>
                  ) : btn;
                })() : null}
                {c.enabled ? (
                  <Button size="small" color="error" disabled={busy}
                    onClick={() => run(() => api.adminGitCredentialRevoke(c.id),
                      'admin.git.revokedOk', (e) => errMsg(e, 'admin.git.error'))}>
                    {t('admin.git.revokeCredential')}
                  </Button>
                ) : null}
              </Stack>
            </Stack>

            {/* ── 绑定归属账号（第一百七十七批，SB-180 (d)）：迁移搬来的旧行没有
                绑定位——密送推导与介绍信都指着它，没有渠道就只剩吊销重签。只对
                「在用且未绑定」显示；已绑定的显示在上方 boundTo，改绑不给按钮
                （轻率改归属邀请错置，真要改走吊销重签或 API）。 ── */}
            {c.enabled && c.accountId == null ? (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
                sx={{ mt: 1.5, alignItems: { md: 'center' } }}>
                <Typography variant="caption" color="warning.main">
                  {t('admin.git.bind.why')}
                </Typography>
                <FormControl size="small"
                  sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 260 } }}>
                  <InputLabel id={`git-bind-${c.id}`}>{t('admin.git.account')}</InputLabel>
                  <Select
                    labelId={`git-bind-${c.id}`}
                    label={t('admin.git.account')}
                    value={bindSel[c.id] ?? ''}
                    onChange={(e) => setBindSel((m) => ({ ...m, [c.id]: e.target.value }))}
                    disabled={busy}
                  >
                    {accountList.map((a) => (
                      <MenuItem key={a.id} value={a.id} sx={{ whiteSpace: 'normal' }}>
                        {a.displayName ? `${a.displayName} · ${a.email}` : a.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button size="small" variant="outlined"
                  disabled={busy || !bindSel[c.id]}
                  onClick={() => run(
                    () => api.adminGitCredentialBind(c.id, bindSel[c.id]),
                    'admin.git.bind.ok', (e) => errMsg(e, 'admin.git.error'))}>
                  {t('admin.git.bind.btn')}
                </Button>
              </Stack>
            ) : null}

            {c.grants.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {t('admin.git.grant.empty')}
              </Typography>
            ) : (
              <Box sx={{ mt: 1.5 }}>
                <DataTable minWidth={560}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('admin.git.repo')}</TableCell>
                      <TableCell>{t('admin.git.canWrite')}</TableCell>
                      <TableCell>{t('admin.git.status')}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {c.grants.map((g) => (
                      <TableRow key={g.id}>
                        <TableCell>{g.repo}</TableCell>
                        <TableCell>
                          <Chip size="small"
                            color={g.canWrite && g.enabled ? 'warning' : 'default'}
                            label={g.canWrite ? t('admin.git.canWrite')
                              : t('admin.git.readOnly')} />
                        </TableCell>
                        <TableCell>
                          <Chip size="small"
                            color={g.enabled ? 'success' : 'default'}
                            label={g.enabled ? t('admin.git.active')
                              : t('admin.git.revoked')} />
                        </TableCell>
                        <TableCell align="right">
                          {g.enabled && c.enabled ? (
                            <Stack direction="row" spacing={0.5}
                              sx={{ justifyContent: 'flex-end' }}>
                              <Button size="small" disabled={busy}
                                onClick={() => run(
                                  () => api.adminGitGrantWrite(c.id, g.id, !g.canWrite),
                                  'admin.git.saved')}>
                                {g.canWrite ? t('admin.git.grant.makeRead')
                                  : t('admin.git.grant.makeWrite')}
                              </Button>
                              <Button size="small" color="error" disabled={busy}
                                onClick={() => run(
                                  () => api.adminGitGrantRevoke(c.id, g.id),
                                  'admin.git.revokedOk')}>
                                {t('admin.git.grant.revoke')}
                              </Button>
                            </Stack>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </DataTable>
              </Box>
            )}

            {/* 加 grant：仓单 − 已生效授权的仓（已吊销的可以重授）。 */}
            {c.enabled ? (
              grantableRepos(repoList, c).length === 0 ? (
                <Typography variant="caption" color="text.secondary"
                  sx={{ display: 'block', mt: 1 }}>
                  {repoList.length === 0 ? t('admin.git.repoEmpty')
                    : t('admin.git.grant.allGranted')}
                </Typography>
              ) : (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
                  sx={{ mt: 1.5, alignItems: { md: 'center' } }}>
                  <FormControl size="small"
                    sx={{ width: { xs: '100%', md: 'auto' }, minWidth: { md: 220 } }}>
                    <InputLabel id={`git-grant-repo-${c.id}`}>{t('admin.git.repo')}</InputLabel>
                    <Select
                      labelId={`git-grant-repo-${c.id}`}
                      label={t('admin.git.repo')}
                      value={gf(c).repo}
                      onChange={(e) => setGf(c, { repo: e.target.value })}
                      disabled={busy}
                    >
                      {grantableRepos(repoList, c).map((name) => (
                        <MenuItem key={name} value={name} sx={{ whiteSpace: 'normal' }}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={<Checkbox checked={gf(c).canWrite}
                      onChange={(e) => setGf(c, { canWrite: e.target.checked })}
                      disabled={busy} />}
                    label={t('admin.git.canWrite')}
                  />
                  <Button size="small" variant="outlined" disabled={busy || !gf(c).repo}
                    onClick={() => onAddGrant(c)}>
                    {t('admin.git.grant.add')}
                  </Button>
                </Stack>
              )
            ) : null}
          </Paper>
        ))
      )}

      <AppSnackbar snack={snack} onClose={clearSnack} />
    </Box>
  );
}
